import React from 'react';
import {
    SectionTitle,
    TierTable,
    CompensationBadge
} from '../styles/BillingStyledComponents';
import {
    computeGroupTotal,
    computeItemUsage,
    findGroupCompensation
} from '../utils/billingPolicyTree';

/**
 * 최종 요금 정산 표 — 정책 트리 구조 그대로 표시.
 *
 * 컬럼: 요금정책 / 그룹 / 항목 | 사용요금 | 보전기준
 *
 * 비교 로직 (행마다):
 *   - 사용요금 < 보전기준  → 보전 적용 → 사용요금에 strikethrough + 이름에 [보전적용] 뱃지
 *   - 사용요금 ≥ 보전기준  → 사용요금 적용 → 보전기준에 strikethrough
 *   - 둘 다 0이면 strikethrough 없음
 *
 * props:
 *   - detail: billingDetail
 */
const BillingFinalTable = ({ detail }) => {
    if (!detail) return null;

    const policyTree = detail.pricingPolicyTree;
    const allGroups = policyTree?.serviceGroupList || [];
    const visibleGroups = allGroups.filter(g => {
        const items = g.servicePriceList || [];
        if (items.length === 0) return false;
        return computeGroupTotal(g, detail) > 0;
    });

    const policyUsage = detail.usageBasedFee != null
        ? detail.usageBasedFee
        : visibleGroups.reduce((sum, g) => sum + computeGroupTotal(g, detail), 0);
    const policyComp = detail.compensationInfo?.policy || 0;

    return (
        <div style={{ marginTop: '1.5rem' }}>
            <SectionTitle>최종 요금 정산</SectionTitle>
            <div style={{ overflowX: 'auto' }}>
                <TierTable>
                    <thead>
                        <tr>
                            <th>요금정책 / 그룹 / 항목</th>
                            <th style={{ textAlign: 'right' }}>사용요금</th>
                            <th style={{ textAlign: 'right' }}>보전기준</th>
                        </tr>
                    </thead>
                    <tbody>
                        <PolicyRow
                            name={detail.pricingPolicyName}
                            version={detail.pricingPolicyVersion}
                            usage={policyUsage}
                            compensation={policyComp}
                        />
                        {visibleGroups.map(group => (
                            <GroupSubtree
                                key={group.uuid || group.serviceItemNumber}
                                group={group}
                                detail={detail}
                            />
                        ))}
                    </tbody>
                </TierTable>
            </div>
        </div>
    );
};

const GroupSubtree = ({ group, detail }) => {
    const groupName = group.serviceItemName || '그룹';
    const groupComp = findGroupCompensation(group, detail.compensationInfo);
    const groupCompAmount = groupComp?.amount || 0;
    const groupUsage = computeGroupTotal(group, detail);
    const items = group.servicePriceList || [];

    return (
        <>
            <GroupRow
                name={groupName}
                usage={groupUsage}
                compensation={groupCompAmount}
            />
            {items.map(item => (
                <ItemRow
                    key={item.uuid || item.serviceItemNumber}
                    item={item}
                    detail={detail}
                />
            ))}
        </>
    );
};

const PolicyRow = ({ name, version, usage, compensation }) => {
    const compensated = compensation > 0 && usage < compensation;
    return (
        <tr style={{ background: '#fef3c7' }}>
            <td style={{ fontWeight: 700, color: '#854d0e' }}>
                {name || '정책'}
                {version != null && (
                    <span style={{ marginLeft: '8px', fontWeight: 600, opacity: 0.85 }}>
                        v{version}
                    </span>
                )}
                {compensated && <CompBadge />}
            </td>
            <td style={{ textAlign: 'right' }}></td>
            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                {compensation > 0 ? `₩${compensation.toLocaleString()}` : ''}
            </td>
        </tr>
    );
};

const GroupRow = ({ name, usage, compensation }) => {
    const compensated = compensation > 0 && usage < compensation;
    return (
        <tr style={{ background: '#eff6ff' }}>
            <td style={{ fontWeight: 600, color: '#1e40af', paddingLeft: '1.5rem' }}>
                <span style={{ color: '#94a3b8', marginRight: '6px' }}>└</span>
                [그룹] {name}
                {compensated && <CompBadge />}
            </td>
            <td style={{ textAlign: 'right' }}></td>
            <td style={{ textAlign: 'right', fontWeight: 500 }}>
                {compensation > 0 ? `₩${compensation.toLocaleString()}` : ''}
            </td>
        </tr>
    );
};

const ItemRow = ({ item, detail }) => {
    const itemName = item.serviceItemName || '항목';
    const itemUsage = computeItemUsage(item.serviceItemNumber, detail);
    const itemComp = parseFloat(item.compensationAmount || 0);
    const usageApplied = itemUsage >= itemComp;
    const compensated = itemComp > 0 && !usageApplied;
    return (
        <tr>
            <td style={{ fontWeight: 500, paddingLeft: '3rem' }}>
                <span style={{ color: '#94a3b8', marginRight: '6px' }}>└</span>
                {itemName}
                {compensated && <CompBadge />}
            </td>
            <ValueCell amount={itemUsage} strikethrough={!usageApplied} />
            <ValueCell amount={itemComp} strikethrough={usageApplied} />
        </tr>
    );
};

const ValueCell = ({ amount, strikethrough, bold = false }) => {
    const formatted = `₩${(amount || 0).toLocaleString()}`;
    const style = {
        textAlign: 'right',
        fontWeight: bold ? 700 : 500,
        textDecoration: strikethrough ? 'line-through' : 'none',
        opacity: strikethrough ? 0.55 : 1
    };
    return <td style={style}>{formatted}</td>;
};

const CompBadge = () => (
    <CompensationBadge style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '0.75rem' }}>
        <span className="comp-label">보전적용</span>
    </CompensationBadge>
);

export default BillingFinalTable;
